
import CategoryDialog from '@/components/modals/CategoryDialog.vue'
import PromptYesNo from '@/components/modals/PromptYesNo.vue'
import LibraryMixin from '@/mixin/Library'
import { CategoryState, LibraryCategoriesUsage, LibraryCategory, LibraryCategoryStates } from '@/store/modules/teamlibrary/types'
import { Namespace } from '@/store/types'
import { CategoryHelpers } from '@/utils/category-helpers'
import { Filters } from '@/utils/filters'
import IconArrowDown from '@svg/IconArrowDown.svg'
import IconArrowUp from '@svg/IconArrowUp.svg'
import IconBin from '@svg/IconBin.svg'
import IconClose from '@svg/IconClose.svg'
import IconInfoFilled from '@svg/IconInfoFilled.svg'
import IconPencil from '@svg/IconPencil.svg'
import IconPlusCircle from '@svg/IconPlusCircle.svg'
import { mixins } from 'vue-class-component'
import { Component, Prop, Watch } from 'vue-property-decorator'
import { RouterLink } from 'vue-router'
import { Action, Mutation, State } from 'vuex-class'

/**
 * Modal component for managing team library categories.
 *
 * Provides functionality to create, update, delete, and reorder categories for organizing
 * team objects in the library. Staff users can also access the public categories management
 * page from this modal.
 *
 * Features:
 * - Create new categories with name, state, and order
 * - Edit category details
 * - Delete category
 * - Reorder categories via up/down arrows
 * - View category usage count (number of objects in each category)
 * - Staff-only link to public categories management
 */
@Component({
  components: {
    CategoryDialog,
    IconArrowDown,
    IconArrowUp,
    IconBin,
    IconClose,
    IconInfoFilled,
    IconPencil,
    IconPlusCircle,
    PromptYesNo,
  },
})
export default class LibraryCategoriesModal extends mixins(LibraryMixin) {
  @Prop({ type: Boolean, required: true }) public value!: boolean
  @Prop({ type: Boolean, default: false }) public isJigSpaceStaff!: boolean
  @Prop({ type: Boolean, default: false }) public isLoading!: boolean

  @State('libraryCategoryStates', { namespace: Namespace.TeamLibrary })
  public libraryCategoryStates!: LibraryCategoryStates
  @State('defaultLibraryCategoryState', { namespace: Namespace.TeamLibrary })
  public defaultLibraryCategoryState!: CategoryState | null
  @State('libraryCategoriesUsage', { namespace: Namespace.TeamLibrary })
  public libraryCategoriesUsage!: LibraryCategoriesUsage

  @Action('createLibraryCategory', { namespace: Namespace.TeamLibrary })
  public createLibraryCategory: any
  @Action('upsertLibraryCategories', { namespace: Namespace.TeamLibrary })
  public upsertLibraryCategories: any
  @Action('deleteLibraryCategoryById', { namespace: Namespace.TeamLibrary })
  public deleteLibraryCategoryById: any
  @Mutation('replaceLibraryCategories', { namespace: Namespace.TeamLibrary })
  public replaceLibraryCategories: any
  @Mutation('setLibraryCategoryOrder', { namespace: Namespace.TeamLibrary })
  public setLibraryCategoryOrder: any

  declare $refs: Vue['$refs'] & {
    deleteCategoryPromptYesNo: PromptYesNo
  }

  private isValid: boolean = false
  private isSubmitting: boolean = false
  private isCategoryModalActive: boolean = false
  private selectedCategory: LibraryCategory | null = null
  private dataDirty: boolean = false
  private readonly unknownCategoryIndex: number = -1
  private readonly hiddenStateId = 5

  private get isVisible(): boolean {
    return this.value
  }

  private set isVisible(val: boolean) {
    this.$emit('input', val)
  }

  private get nextCategoryOrder(): number {
    if (this.libraryCategories == null || this.libraryCategories.Categories == null || this.libraryCategories.Categories.length === 0) {
      return 0
    }

    const maxOrder = Math.max(...this.libraryCategories.Categories.map((cat) => cat.CategoryOrder))
    return maxOrder + 1
  }

  @Watch('value')
  private onValueChanged(newVal: boolean) {
    if (!newVal) {
      // Reset state when dialog closes
      this.resetState()
    }
  }

  protected mounted() {
    // Component initialization if needed
  }

  private categoryUpdatedDate(datetime: string) {
    return Filters.formatDate(datetime, undefined, true)
  }

  private updateCategory(category: LibraryCategory) {
    this.selectedCategory = category
    this.showCreateCategoryModal()
  }

  private showCreateCategoryModal() {
    this.isCategoryModalActive = true
  }

  private onSubmitCancel() {
    this.resetUpdateModalState()
  }

  private async submitCategory(categoryData: any) {
    this.isSubmitting = true

    try {
      if (this.selectedCategory === null) {
        // Create new category
        await this.createLibraryCategory({
          DisplayName: categoryData.DisplayName,
          CategoryOrder: Number(categoryData.CategoryOrder),
          StateId: categoryData.StateId,
          TenantId: this.myTenantID,
        })
      } else {
        // Update existing category
        const updatedCategory = {
          ...this.selectedCategory,
          DisplayName: categoryData.DisplayName,
          CategoryOrder: Number(categoryData.CategoryOrder),
          StateId: categoryData.StateId,
          StateName: categoryData.StateName,
        }
        await this.upsertLibraryCategories({
          categories: [updatedCategory],
          tenantId: this.myTenantID,
        })
      }

      // Reload categories after successful operation
      await this.loadLibraryCategories(this.myTenantID)

      // Close the modal and reset state
      this.resetUpdateModalState()
    } finally {
      this.isSubmitting = false
    }
  }

  private resetUpdateModalState() {
    this.isCategoryModalActive = false
    this.selectedCategory = null
  }

  private showDeleteCategoryPrompt(category: LibraryCategory) {
    this.selectedCategory = category
    this.$refs.deleteCategoryPromptYesNo.ShowPrompt()
  }

  private async deleteCategoryById() {
    if (this.selectedCategory == null) {
      return
    }

    this.isSubmitting = true

    try {
      // Wait for deletion to complete
      await this.deleteLibraryCategoryById(this.selectedCategory.CategoryId)

      // Reload categories after successful deletion
      await this.loadLibraryCategories(this.myTenantID)

      this.closeDeleteModal()
    } finally {
      this.isSubmitting = false
    }
  }

  private closeDeleteModal() {
    if (this.$refs.deleteCategoryPromptYesNo) {
      this.$refs.deleteCategoryPromptYesNo.ClosePrompt()
    }
    this.resetDeleteModalState()
  }

  private resetDeleteModalState() {
    this.selectedCategory = null
  }

  private onCancel() {
    this.isVisible = false
    this.$emit('cancel')
    this.closeDeleteModal()
    this.resetState()
  }

  private resetState() {
    this.isValid = false
    this.resetUpdateModalState()
  }

  private getUsageCount(categoryId: number): number {
    const usage = this.libraryCategoriesUsage.usageMap[categoryId]
    return usage != null ? usage : 0
  }

  private canMoveUp(category: LibraryCategory, sortedCats: LibraryCategory[]): boolean {
    return CategoryHelpers.canMoveUp(category, sortedCats)
  }

  private canMoveDown(category: LibraryCategory, sortedCats: LibraryCategory[]): boolean {
    return CategoryHelpers.canMoveDown(category, sortedCats)
  }

  private async moveCategory(category: LibraryCategory, direction: number) {
    if (this.libraryCategories == null || this.libraryCategories.Categories == null) {
      return
    }

    this.isSubmitting = true

    const updatedCategories = this.libraryCategories.Categories.map((item) => ({
      ...item,
    }))
    const targetCategory = updatedCategories.find((item) => item.CategoryId === category.CategoryId)
    if (targetCategory == null) {
      this.isSubmitting = false
      return
    }

    const success = CategoryHelpers.moveCategory(targetCategory, direction, updatedCategories, this.unknownCategoryIndex)

    if (success) {
      this.replaceLibraryCategories(updatedCategories)
      this.recalculateCategoryOrderList()
      this.dataDirty = true
      await this.saveCategories()
    }
    this.isSubmitting = false
  }

  private recalculateCategoryOrderList() {
    if (this.libraryCategories == null) {
      return
    }

    const newOrder = CategoryHelpers.recalculateCategoryOrderList(this.libraryCategories.Categories, this.hiddenStateId, true)

    this.setLibraryCategoryOrder(newOrder)
  }

  private async saveCategories() {
    if (!this.dataDirty || this.libraryCategories == null) {
      return
    }

    await this.upsertLibraryCategories({
      categories: this.libraryCategories.Categories,
      tenantId: this.myTenantID,
    })
    await this.loadLibraryCategories(this.myTenantID)
    this.dataDirty = false
  }
}
